import { useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import type { AdsEntityLevel, AdsManagerSavedDailyData, AdsManagerSavedEntity, AdsManagerSavedEntityPage } from "@shared/ads-manager";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function savedReportingEntity(row: AdsManagerSavedEntity) {
  return {
    id: row.id, name: row.name, level: row.level,
    campaignId: row.campaignId, adsetId: row.adsetId,
    configuredStatus: "UNKNOWN", effectiveStatus: "UNKNOWN",
    deliveryReadiness: "historical_reporting" as const,
    metrics: {
      ...row.metrics,
      cpm: row.metrics.impressions > 0 ? row.metrics.spend * 1000 / row.metrics.impressions : null,
      ctr: row.metrics.impressions > 0 ? row.metrics.clicks * 100 / row.metrics.impressions : null,
    },
    // Keep the provider-selected result separate from full-action/eligible-lead totals.
    savedOutcome: row.outcome,
  };
}
export type SavedReportingEntity = ReturnType<typeof savedReportingEntity>;
export type SavedReportingView = {
  day: AdsManagerSavedDailyData["days"][number] | undefined;
  level: AdsEntityLevel;
  campaign: SavedReportingEntity | null;
  adset: SavedReportingEntity | null;
  rows: SavedReportingEntity[];
  search: string;
  setSearch: (value: string) => void;
  drillCampaign: (row: SavedReportingEntity) => void;
  drillAdset: (row: SavedReportingEntity) => void;
  reset: () => void;
  clearAdset: () => void;
  offset: number;
  nextOffset: number | null;
  setOffset: (value: number) => void;
  pending: boolean;
  error: string | null;
};
const date = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString(undefined, {
  month: "short", day: "numeric", year: "numeric", timeZone: "UTC",
});
const coverage = (day: AdsManagerSavedDailyData["days"][number]) => day.availability === "missing"
  ? "Missing · unknown, not zero" : day.availability === "incompatible" ? "Incompatible saved certificate · unavailable"
    : `${day.status === "partial" ? "Partial" : "Completed"} · ${day.fresh === null ? "collection age unknown" : day.fresh ? "fresh" : "stale"}`;
const timestamp = (value: string | null, timezone: string) =>
  value ? new Date(value).toLocaleString(undefined, { timeZone: timezone, timeZoneName: "short" }) : "Unknown";
const collectionAge = (value: string | null) => {
  if (!value) return "Unknown";
  const collectedAt = new Date(value).getTime();
  if (!Number.isFinite(collectedAt)) return "Unknown";
  const minutes = Math.max(0, Math.floor((Date.now() - collectedAt) / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hr${hours === 1 ? "" : "s"}`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
};

/** Evidence owner only. Both sources are rendered by the established reporting presentation. */
export function SavedDailyData(props: {
  data: AdsManagerSavedDailyData;
  reportingHeader?: HTMLElement | null;
  renderDataDetails?: (selectedDayDetails: ReactNode) => ReactNode;
  children: (view: SavedReportingView) => ReactNode;
}) {
  return <SavedDailyBrowser key={JSON.stringify([props.data.brandId, props.data.accountId, props.data.range, props.data.accountGeneration])} {...props} />;
}
function SavedDailyBrowser({ data, reportingHeader, renderDataDetails, children }: Parameters<typeof SavedDailyData>[0]) {
  const [dayId, setDay] = useState(() => [...data.days].reverse().find(day => day.availability === "saved")?.day ?? data.days[0]?.day ?? "");
  const [level, setLevel] = useState<AdsEntityLevel>("campaign");
  const [campaign, setCampaign] = useState<SavedReportingEntity | null>(null);
  const [adset, setAdset] = useState<SavedReportingEntity | null>(null);
  const [search, setSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const day = data.days.find(item => item.day === dayId);
  const params = new URLSearchParams({ since: data.range.since, until: data.range.until, accountId: data.accountId,
    savedDay: dayId, savedLevel: level, savedGeneration: String(data.accountGeneration),
    savedOffset: String(offset), savedSearch: search,
    ...(campaign && { savedCampaign: campaign.id }), ...(adset && { savedAdset: adset.id }) });
  const entities = useQuery<AdsManagerSavedEntityPage>({
    queryKey: ["ads-manager-saved-entities", data.brandId, params.toString()],
    enabled: day?.availability === "saved" && data.accountGeneration !== undefined,
    retry: false,
    queryFn: async ({ signal }) => {
      const response = await fetch(`/api/brands/${data.brandId}/ads-manager?${params}`, { credentials: "include", signal });
      if (!response.ok) throw new Error("Saved data could not be loaded. Reload this report to check its scope.");
      const body: AdsManagerSavedEntityPage = await response.json();
      if (body.brandId !== data.brandId || body.accountId !== data.accountId
        || body.accountGeneration !== data.accountGeneration || body.range.since !== data.range.since
        || body.range.until !== data.range.until || body.day !== dayId || body.level !== level
        || body.campaignId !== (campaign?.id ?? null) || body.adsetId !== (adset?.id ?? null)
        || body.offset !== offset || body.availability !== "saved")
        throw new Error("Saved data did not match this view. Reload this report.");
      return body;
    },
  });
  const reset = () => { setLevel("campaign"); setCampaign(null); setAdset(null); setOffset(0); setSearch(""); };
  const selectedDayDetails = day ? <section aria-label="Selected day evidence" className="space-y-3">
    <h2 className="text-sm font-semibold">Selected day evidence</h2>
    <p className="text-muted-foreground">{date(day.day)} only · {data.timezone}</p>
    <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
      <div><dt className="text-muted-foreground">Daily coverage</dt><dd className="font-medium">{coverage(day)}</dd></div>
      {day.availability === "saved" && <>
        <div><dt className="text-muted-foreground">Collected at</dt><dd>{timestamp(day.collectedAt, data.timezone)}</dd></div>
        <div><dt className="text-muted-foreground">Collection age</dt><dd>{day.collectedAt ? `${collectionAge(day.collectedAt)} ago` : "Unknown"} · {day.fresh === null ? "age qualification unknown" : day.fresh ? "fresh by collection age" : "stale by collection age"}</dd></div>
        <div><dt className="text-muted-foreground">Oldest provider timestamp</dt><dd>{timestamp(day.sourceOldestAt, data.timezone)}</dd></div>
        <div><dt className="text-muted-foreground">Newest provider timestamp</dt><dd>{timestamp(day.sourceNewestAt, data.timezone)}</dd></div>
        {day.metrics && <div><dt className="text-muted-foreground">Recorded daily totals</dt><dd>
          {day.currency ? new Intl.NumberFormat(undefined, { style: "currency", currency: day.currency }).format(day.metrics.spend) : `${day.metrics.spend} (currency unknown)`}
          {" · "}{day.metrics.impressions.toLocaleString()} impressions · {day.metrics.clicks.toLocaleString()} clicks
        </dd></div>}
      </>}
    </dl>
    <p className="text-xs text-muted-foreground">Collection age is not provider freshness. Only recorded entities are available; missing entities are unknown, not zero. Outcomes use provider-selected results and default attribution, not full-action purchase or eligible-lead totals.</p>
    <h3 className="text-sm font-medium">Daily coverage within selected dates</h3>
    <ul className="space-y-2 text-sm">{data.days.map(item => <li key={item.day} data-testid={`saved-day-${item.day}`}>
      <span className="font-medium">{date(item.day)}</span> · {coverage(item)}
      {item.metrics && <> · {item.currency ? new Intl.NumberFormat(undefined, { style: "currency", currency: item.currency }).format(item.metrics.spend) : `${item.metrics.spend} (currency unknown)`} · {item.metrics.impressions.toLocaleString()} impressions · {item.metrics.clicks.toLocaleString()} clicks</>}
    </li>)}</ul>
  </section> : <p className="text-sm text-muted-foreground">No saved reporting dates are available.</p>;
  const statusBadge = (label: string, tone: string) => <span key={label} className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${tone}`}>{label}</span>;
  const controls = <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2" data-testid={day ? `saved-selected-day-${day.day}` : undefined}>
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <label id="saved-reporting-date-label" htmlFor="saved-reporting-date" className="text-xs font-medium">Saved date</label>
      <Select value={dayId} onValueChange={value => { setDay(value); reset(); }} disabled={data.days.length === 0}>
        <SelectTrigger id="saved-reporting-date" aria-label="Saved reporting date" className="h-8 w-[10rem] max-w-full rounded-full text-xs">
          <SelectValue>{day ? date(day.day) : "Select a date"}</SelectValue>
        </SelectTrigger>
        <SelectContent position="popper" align="start" className="max-w-[calc(100vw-2rem)]">
          {data.days.map(item => <SelectItem key={item.day} value={item.day}>
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"><span>{date(item.day)}</span><span className="text-xs text-muted-foreground">{coverage(item)}</span></span>
          </SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex flex-wrap items-center gap-1.5" aria-label="Reporting status">
        <span title="The selected-range report is incomplete; a completed saved day is not a complete range report">{statusBadge("Partial", "border-amber-700/20 bg-amber-50 text-amber-900")}</span>
        {day?.availability === "saved" && day.fresh === false && statusBadge("Stale", "border-orange-700/20 bg-orange-50 text-orange-900")}
        {day?.availability === "missing" && statusBadge("Missing", "border-muted-foreground/30 bg-muted text-muted-foreground")}
        {day?.availability === "incompatible" && statusBadge("Incompatible", "border-destructive/30 bg-destructive/5 text-destructive")}
      </div>
    </div>
    {renderDataDetails ? renderDataDetails(selectedDayDetails) : selectedDayDetails}
  </div>;
  const usableDay = day?.availability === "saved";
  return <>
    {reportingHeader === undefined ? controls : reportingHeader ? createPortal(controls, reportingHeader) : null}
    <p role="note" className="rounded-lg border bg-muted/30 px-3 py-2 text-sm" data-testid="ads-manager-incomplete-disclaimer">
      Reporting is incomplete. {usableDay ? `Showing ${day.status === "partial" ? "partial " : ""}${day.fresh === false ? "stale " : ""}saved facts for ${date(day.day)} only, not totals for the selected range.` : "No compatible saved facts are available for this reporting date; values are unknown, not zero."} Saved reporting does not confirm current delivery status.
    </p>
    <div data-testid="ads-manager-saved-daily-data">{children({
      day, level, campaign, adset,
      rows: usableDay && !entities.isError ? (entities.data?.rows ?? []).map(row => ({ ...savedReportingEntity(row), currency: day.currency })) : [],
      search, setSearch: value => { setSearch(value); setOffset(0); },
      drillCampaign: row => { setCampaign(row); setAdset(null); setLevel("adset"); setOffset(0); setSearch(""); },
      drillAdset: row => { setAdset(row); setLevel("ad"); setOffset(0); setSearch(""); },
      reset, clearAdset: () => { setAdset(null); setLevel("adset"); setOffset(0); setSearch(""); },
      offset, nextOffset: entities.data?.nextOffset ?? null, setOffset,
      pending: usableDay && data.accountGeneration !== undefined && entities.isFetching,
      error: entities.isError ? entities.error.message : data.accountGeneration === undefined
        ? "Saved entity scope is unavailable. Reload this report." : null,
    })}</div>
  </>;
}
